'use client'
import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

type Story = {
  name: string
  stat: string
  stat_label: string
  text: string
  image: string
  href: string
}

type Button = { text: string; href: string; variant: 'Contained' | 'Text'; target?: '_blank' }

export interface StoriesSlice extends SliceLike<'stories'> {
  variation: 'default'
  primary: {
    eyebrow: string
    title: string
    text: string
    stories: Story[]
    /** Optional CTA row under the cards; the homepage uses it for Book a Demo. */
    buttons?: Button[]
  }
}

export type StoriesProps = SliceComponentProps<StoriesSlice>

/** Customer stories on ink: two big photo cards with the real screen counts. */
const Stories: FC<StoriesProps> = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="bg-ink relative overflow-hidden py-20 text-white md:py-28"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-80 left-1/2 h-[700px] w-[1200px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(129,12,168,0.4),transparent)] blur-2xl"
    />
    <div className="relative container">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="max-w-[640px]">
          <p className="eyebrow-light">{slice.primary.eyebrow}</p>
          <h2 className="display-title mt-3 text-white">{slice.primary.title}</h2>
        </div>
        <p className="max-w-[420px] text-[17px] leading-7 text-white/70">{slice.primary.text}</p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
        {slice.primary.stories.map((story, i) => (
          <motion.div
            key={story.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, delay: i * 0.1, ease: 'easeOut' }}
          >
            <Link
              href={story.href}
              className="group relative block aspect-[4/3] overflow-hidden rounded-[28px] ring-1 ring-white/10 md:aspect-[5/4]"
            >
              <Image
                src={story.image}
                alt={story.name}
                fill
                sizes="(min-width: 768px) 50vw, 100vw"
                className="object-cover transition duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#120616] via-[#120616]/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-7 md:p-8">
                <div>
                  <p className="font-rubik text-signal text-[44px] leading-none font-semibold tracking-tight md:text-[56px]">
                    {story.stat}
                  </p>
                  <p className="mt-1 text-[13px] font-semibold tracking-wide text-white/70 uppercase">
                    {story.stat_label}
                  </p>
                  <h3 className="font-rubik mt-4 text-[24px] font-semibold">{story.name}</h3>
                  <p className="mt-1 max-w-[40ch] text-[15px] leading-6 text-white/75">{story.text}</p>
                </div>
                <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white text-[#120616] transition group-hover:bg-[#c147e9] group-hover:text-white">
                  <IconComponent icon="ChevronRight" size="xsmall" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {slice.primary.buttons && slice.primary.buttons.length > 0 && (
        <div className="mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6">
          {slice.primary.buttons.map((b) => (
            <a
              key={b.text}
              href={b.href}
              target={b.target}
              rel={b.target === '_blank' ? 'noopener noreferrer' : undefined}
              className={
                b.variant === 'Contained'
                  ? 'bg-primary hover:bg-signal inline-flex items-center justify-center rounded-full px-7 py-3.5 text-[16px] font-semibold text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)] transition'
                  : 'inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-[16px] font-semibold text-white transition hover:bg-white/10'
              }
            >
              {b.text}
              {b.variant !== 'Contained' && (
                <IconComponent icon="ChevronRight" size="xsmall" className="text-signal" />
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  </section>
)

export default Stories
